import * as React from 'react';
import * as ReactDOM from 'react-dom';
import {Dialog} from '../../../shared-design-system/components/Dialog/Dialog';
import {Button} from '../../../shared-design-system/components/Button/Button';
import {ButtonGroup} from '../../../shared-design-system/components/ButtonGroup/ButtonGroup';
import styles from './ConfirmationDialog.module.css';
import {classes} from '../../helpers/RenderHelpers';
import {ReactElement} from 'react';
import SingletonDialogService from '../../services/SingletonDialogService';
import {isEmptyOrWhiteSpace} from '../../helpers/StateHelpers';
import DistributeContainer from '../../../shared-ui/components/DistributeContainer/DistributeContainer';
import If from '../../../shared-ui/components/If/If';
import Helpers from '../../../shared-common/helpers/Helpers';

export enum ConfirmationType {
  Success,
  Question,
  Exclamation
}

type ConfirmationDialogContentFunc = (closeCallback: () => void) => ReactElement<any>;

export interface ConfirmationDialogOptions {
  title: string;
  content: string | React.ReactNode | ConfirmationDialogContentFunc;
  maxWidth?: number;
  type?: ConfirmationType;
  className?: string;
  okText?: string | React.ReactNode;
  okIcon?: string | React.ReactNode;
  showCancel?: boolean;
  cancelText?: string | React.ReactNode;
  singletonDialogGroupCode?: string;
  footerTraceMessage?: string;
}

export default class ConfirmationDialog {
  private static DEFAULT_MAX_WIDTH_PIXELS = 520;

  public static show = async (options: ConfirmationDialogOptions): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {

      const deregister = () => {
        if (options.singletonDialogGroupCode) {
          SingletonDialogService.deregisterAnonymousDialogs(options.singletonDialogGroupCode);
        }
      };

      const onOk = () => {
        ConfirmationDialog.render(mountingDiv, onOk, onCancel, options, true);
        deregister();
        resolve(true);
      };

      const onCancel = () => {
        ConfirmationDialog.render(mountingDiv, onOk, onCancel, options, true);
        deregister();
        resolve(false);
      };

      if (options.singletonDialogGroupCode) {
        SingletonDialogService.registerAnonymousDialog(options.singletonDialogGroupCode, onCancel);
      }

      const mountingDiv = document.createElement('div');
      document.body.appendChild(mountingDiv);
      ConfirmationDialog.render(mountingDiv, onOk, onCancel, options);
    });
  };

  private static render = (mountingDiv: HTMLDivElement,
                           onOk: () => void,
                           onCancel: () => void,
                           options: ConfirmationDialogOptions,
                           isClosing?: boolean) => {
    ReactDOM.render(
      ConfirmationDialog.createWindow(mountingDiv, onOk, onCancel, options, isClosing),
      mountingDiv
    );
  };

  private static mapIcon = (icon: string | undefined): 'accept' | 'cancel' | undefined => {
    switch (icon) {
      case 'check-circle-o': return 'accept';
      case 'cross-circle-o': return 'cancel';
      case 'close-circle-o': return 'cancel';
      default: return undefined;
    }
  };

  private static renderHeadingIcon = (type?: ConfirmationType) => {
    if (type === ConfirmationType.Exclamation) {
      return (
        <svg className={classes(styles.headingIcon, styles.exclamation)} viewBox="0 0 1024 1024" width="1em" height="1em" fill="currentColor">
          <path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.4 0-372-166.6-372-372s166.6-372 372-372 372 166.6 372 372-166.6 372-372 372z"/>
          <path d="M464 688a48 48 0 1 0 96 0 48 48 0 1 0-96 0zm24-112h48c4.4 0 8-3.6 8-8V296c0-4.4-3.6-8-8-8h-48c-4.4 0-8 3.6-8 8v272c0 4.4 3.6 8 8 8z"/>
        </svg>
      );
    }
    if (type === ConfirmationType.Success) {
      return (
        <svg className={classes(styles.headingIcon, styles.success)} viewBox="0 0 1024 1024" width="1em" height="1em" fill="currentColor">
          <path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.4 0-372-166.6-372-372s166.6-372 372-372 372 166.6 372 372-166.6 372-372 372z"/>
          <path d="M699 353h-46.9c-10.2 0-19.9 4.9-25.9 13.3L469 584.3l-71.2-98.8c-6-8.3-15.6-13.3-25.9-13.3H325c-6.5 0-10.3 7.4-6.5 12.7l124.6 172.8a31.8 31.8 0 0 0 51.7 0l210.6-292c3.9-5.3.1-12.7-6.4-12.7z"/>
        </svg>
      );
    }
    // Question type (default)
    return (
      <svg className={styles.headingIcon} viewBox="0 0 1024 1024" width="1em" height="1em" fill="currentColor">
        <path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.4 0-372-166.6-372-372s166.6-372 372-372 372 166.6 372 372-166.6 372-372 372z"/>
        <path d="M623.6 316.7C593.6 290.4 554 276 512 276c-42 0-81.6 14.4-111.6 40.7-30.8 27-48.4 63.4-48.4 99.8v1.5c0 4.4 3.6 8 8 8h56c4.4 0 8-3.6 8-8v-1.5c0-22.8 10.4-44 28.5-58.9C473 340 492 332 512 332s39 8 59.5 25.6c18.1 14.9 28.5 36.1 28.5 58.9 0 33.5-18.5 46.5-55.5 68.5-42.5 25.2-70.5 56-70.5 104v6c0 4.4 3.6 8 8 8h56c4.4 0 8-3.6 8-8v-4c0-23 10-38 53-62 47.5-26.5 83-60 83-117 0-36.4-17.6-72.8-48.4-99.8zM472 680a40 40 0 1 0 80 0 40 40 0 1 0-80 0z"/>
      </svg>
    );
  };

  private static createWindow = (mountingDiv: HTMLDivElement,
                                  onOk: () => void,
                                  onCancel: () => void,
                                  options: ConfirmationDialogOptions,
                                  isClosing?: boolean) => {

    const okIcon = (!options.okIcon || typeof options.okIcon === 'string')
      ? ConfirmationDialog.mapIcon(options.okIcon as string || 'check-circle-o')
      : undefined;

    let buttons = [
      <Button
        key="1"
        onClick={onOk}
        variant={options.type === ConfirmationType.Exclamation ? 'danger' : 'primary'}
        icon={typeof options.okIcon === 'string' ? okIcon : (options.okIcon || okIcon)}
        text={typeof options.okText === 'string' ? (options.okText || 'OK') : (!options.okText ? 'OK' : undefined)}
      >
        {(options.okText && typeof options.okText !== 'string') ? options.okText : null}
      </Button>
    ];

    if (options.showCancel !== false && options.type !== ConfirmationType.Success) {
      buttons.unshift(<Button
        key="0"
        onClick={onCancel}
        icon="cancel"
        text={options.cancelText && !isEmptyOrWhiteSpace(options.cancelText)
          ? (typeof options.cancelText === 'string' ? options.cancelText : undefined)
          : 'Cancel'}
      >
        {typeof options.cancelText !== 'string' ? options.cancelText : null}
      </Button>);
    }

    return (
      <Dialog
        title={null}
        footer={
          <DistributeContainer verticalAlignment="center">
            <div>
              <If condition={!Helpers.isBlank(options.footerTraceMessage)}>
              <span
                style={{
                  fontSize: '12px',
                  fontFamily: 'Open Sans, sans-serif',
                  color: 'rgba(0, 0, 0, 0.35)',
                  marginBottom: 10
                }}
              >
                {options.footerTraceMessage}
              </span>
              </If>
            </div>
            <ButtonGroup>{buttons}</ButtonGroup>
          </DistributeContainer>
        }
        onConfirm={onOk}
        onDismiss={onCancel}
        isOpen={!isClosing}
        width={options.maxWidth || ConfirmationDialog.DEFAULT_MAX_WIDTH_PIXELS}
        className={classes(styles.dialog, options.className)}
        isDraggable={false}
        isResizable={false}
        afterClosed={() => {
          const wasUnmounted = ReactDOM.unmountComponentAtNode(mountingDiv);
          if (wasUnmounted && mountingDiv.parentNode) {
            mountingDiv.parentNode.removeChild(mountingDiv);
          }
        }}
      >
        <div className={styles.heading}>
          {ConfirmationDialog.renderHeadingIcon(options.type)}
          <span className={styles.headingText}>{options.title}</span>
        </div>
        <div className={styles.content}>
          {ConfirmationDialog.getContent(options, onOk)}
        </div>
      </Dialog>
    );
  };

  private static getContent = (options: ConfirmationDialogOptions, closeCallback: () => void): React.ReactNode => {
    if (typeof options.content === 'function') {
      return (options.content as ConfirmationDialogContentFunc)(closeCallback);
    } else {
      return options.content;
    }
  };
}
